import type { AiProfile } from "@/types/ai";
import { eventBus } from "@/lib/utils/eventBus";

export interface CouncilTile {
  id: string;
  name: string;
  profile: AiProfile;
}

export interface CouncilRoundResult {
  tile: CouncilTile;
  text: string;
}

const askOne = async (profile: AiProfile, message: string, userId: string): Promise<string> => {
  const res = await fetch("/api/karma-ac/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile, message, userId, stream: false }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return typeof data?.message === "string" ? data.message : "";
};

const announce = (tiles: CouncilTile[], label: string) => {
  tiles.forEach((t) => eventBus.emit("SPATIAL:CONFERRING", { tileId: t.id, label }));
};

export const clearConferring = (tiles: CouncilTile[]) => {
  tiles.forEach((t) => eventBus.emit("SPATIAL:CONFERRING", { tileId: t.id, label: null }));
};

/**
 * Round 1 only: every member answers independently, no cross-talk yet.
 * Returned to the caller (CouncilPanel, via MindalooShell) so the user can
 * see where everyone landed and optionally steer round 2 toward one of
 * them, instead of the deliberation picking a direction on its own.
 *
 * sessionContext, when set, is the prior verdict from an earlier question
 * in this same Council session (see MindalooShell's councilSession state) —
 * folding it in means a follow-up question starts already grounded in
 * whatever the room settled on last time, instead of re-litigating from
 * zero. Cleared whenever the panel closes, so it never survives past the
 * sitting it came from.
 */
export async function getCouncilRound1(
  text: string,
  tiles: CouncilTile[],
  userId: string,
  sessionContext?: string | null
): Promise<CouncilRoundResult[]> {
  announce(tiles, `Brainstorming with ${tiles.length - 1} other${tiles.length - 1 === 1 ? "" : "s"}…`);
  const prompt = sessionContext
    ? `Earlier in this session, the council settled on:\n${sessionContext}\n\nNew question, building on that same direction unless it clearly calls for a different one: ${text}`
    : text;
  try {
    return await Promise.all(tiles.map(async (t) => ({ tile: t, text: await askOne(t.profile, prompt, userId) })));
  } catch (err) {
    clearConferring(tiles);
    throw err;
  }
}

export interface CouncilDeliberationResult {
  /** Each participant's own round-2 refinement — distinct text per tile,
   *  in their own voice, having read what the others said. This is what
   *  belongs injected into each participant's own tile: a real "I agree
   *  with X because Y" / "I'd go differently here" response. Previously
   *  this got computed and then thrown away — every tile was injected
   *  with the identical `synthesis` text below instead, which is why
   *  five distinct AI personalities all appeared to say the same thing. */
  round2: CouncilRoundResult[];
  /** Karma's single-verdict synthesis across all of round 2 — real
   *  session memory for a follow-up question (see MindalooShell's
   *  councilSession), and a standalone summary worth showing once in the
   *  Council panel itself, but NOT something that belongs broadcast into
   *  every participant's own chat history. */
  synthesis: string;
}

/**
 * Round 2 + synthesis, resuming after getCouncilRound1. steerTileId, when
 * set, is the round-1 answer the user picked to steer toward — everyone
 * else's refinement is framed as building on that pick specifically,
 * rather than weighing all round-1 answers equally. Fixed shape (one
 * refinement round, one synthesis), not "keep going until they agree" —
 * see the module-level rationale this replaced in git history.
 */
export async function continueCouncilDeliberation({
  text,
  tiles,
  round1,
  steerTileId,
  userId,
}: {
  text: string;
  tiles: CouncilTile[];
  round1: CouncilRoundResult[];
  steerTileId?: string | null;
  userId: string;
}): Promise<CouncilDeliberationResult> {
  try {
    announce(tiles, "Comparing notes…");
    const steered = steerTileId ? round1.find((r) => r.tile.id === steerTileId) : null;
    const round2 = await Promise.all(
      round1.map(async (r, i) => {
        let prompt: string;
        if (steered) {
          prompt = r.tile.id === steered.tile.id
            ? `Original question: ${text}\n\nYou're being asked to lead — the group wants to build on your take specifically:\n${r.text}\n\nGive your refined, final-form version of it. Be concise, this is a private discussion round, not your final answer.`
            : `Original question: ${text}\n\nThe group has chosen to build on ${steered.tile.name}'s take:\n${steered.text}\n\nYour own initial take was:\n${r.text}\n\nGiven that the group is steering this direction, refine your view to build on ${steered.tile.name}'s answer — add what's missing, or say plainly where you'd still go differently. Be concise, this is a private discussion round, not your final answer.`;
        } else {
          const others = round1
            .filter((_, j) => j !== i)
            .map((o) => `${o.tile.name}: ${o.text}`)
            .join("\n\n");
          prompt = `Original question: ${text}\n\nYour teammate${round1.length > 2 ? "s" : ""} said:\n${others}\n\nGiven that, give your refined take — agree and build on it, add what's missing, or say plainly where you'd go differently. Speak in your own voice, like you're actually in the room replying to them — it's fine to just say you agree and why, you don't need to pad it out.`;
        }
        return { tile: r.tile, text: await askOne(r.tile.profile, prompt, userId) };
      })
    );

    announce(tiles, "Karma is weighing the final answer…");
    const allViews = round2.map((r) => `${r.tile.name}: ${r.text}`).join("\n\n");
    const synthesisPrompt = `Original question: ${text}\n\nHere's what each member of the council concluded after discussing it together${steered ? `, building on ${steered.tile.name}'s direction` : ""}:\n${allViews}\n\nAs Karma, give the council's final, single answer — not a summary of who said what, the actual verdict, as if the room reached it together and you're the one delivering it.`;
    const synthesisProfile = (steered ?? round2[0]).tile.profile;
    const finalText = await askOne(synthesisProfile, synthesisPrompt, userId);
    return { round2, synthesis: finalText || allViews };
  } finally {
    clearConferring(tiles);
  }
}
